import { Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { Toaster } from 'sonner';
import { RootLayout } from './app/RootLayout';
import { useAuthStore } from './shared/stores/authStore';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { PageSpinner, SkeletonGrid, SkeletonPage, SkeletonList } from './shared/components/SkeletonLoader';
import { useOnboarding } from './shared/hooks/useOnboarding';
import { useAnalytics, usePageViewTracker } from './core/analytics/useAnalytics';
import { ThemeProvider } from './shared/components/ThemeProvider';
import { ReducedMotionProvider } from './shared/components/motion';
import {
  OnboardingWizard, LandingPage, TrialPage, PlazaPage, CreativePanel,
  StudioLayout, StudioIndexRedirect, SeedPage, FoundationPage,
  LoginPage, RegisterPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage,
  NovelDetailPage, ReaderPage, GenrePage, GenreListPage, BookshelfPage,
  OutlinePage, WritingPage, PublishPage, CodexPage, ImportExportPage, NovelSettingsPage,
  ProfileSettingsPage, UserProfilePage,
  AdminLayout, AdminDashboardPage, ReviewQueuePage, ReviewDetailPage,
  ModActionLogPage, ReportManagePage, UserManagePage, CommentAuditPage,
  SystemConfigPage, AdminNovelManager, UserDetailPage, ArenaAdminPage,
  ArenaHomePage, RoundDetailPage, SeedDetailPage, MyArenaPage, OutlineReviewPage,
  DerivativesHomePage, SourceDerivativesPage, DerivativeTreePage,
  CommunityPage,
} from './lazyPages';

function PageFallback() {
  return <PageSpinner />;
}

/** Plaza-specific fallback — grid of novel card skeletons */
function PlazaFallback() {
  return (
    <div className="p-6">
      <SkeletonGrid count={8} cols={4} />
    </div>
  );
}

/** Content-page fallback — text skeleton for reading/detail pages */
function ContentFallback() {
  return <SkeletonPage lines={10} />;
}

/** List-page fallback — list skeletons for workshop/publish */
function ListFallback() {
  return (
    <div className="p-6">
      <SkeletonList count={4} height={80} />
    </div>
  );
}

/** 404 page for unmatched routes */
function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <p className="text-6xl font-bold text-muted-foreground/30">404</p>
      <p className="text-lg text-muted-foreground">页面不存在</p>
      <a href="/" className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">返回首页</a>
    </div>
  );
}

/** Route guard — redirects to login if not authenticated */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  if (!initialized) return <PageFallback />;
  if (!user) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

/** Home route — everyone goes straight to plaza (landing page moved to /about) */

/** Renders nothing — just runs the page view tracker inside BrowserRouter context */
function PageViewTrackerInner() {
  usePageViewTracker();
  return null;
}

export function App() {
  // Initialize auth on app start — listens for session changes
  const initialize = useAuthStore((s) => s.initialize);
  useEffect(() => { initialize(); }, [initialize]);
  const onboarding = useOnboarding();
  useAnalytics();

  return (
    <ReducedMotionProvider>
    <ThemeProvider>
    <BrowserRouter>
      <PageViewTrackerInner />
      <Toaster position="top-center" richColors closeButton duration={4000} />
      {onboarding.shouldShow && (
        <Suspense fallback={null}>
          <OnboardingWizard onComplete={onboarding.complete} onSkip={onboarding.skip} />
        </Suspense>
      )}
      <Routes>
        {/* Home → redirect to plaza; landing page at /about */}
        <Route index element={<Navigate to="/plaza" replace />} />
        <Route path="about" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><LandingPage /></ErrorBoundary></Suspense>} />
        <Route path="trial" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><TrialPage /></ErrorBoundary></Suspense>} />

        {/* Auth — 认证页面，独立布局 (split-screen) */}
        <Route path="auth/login" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><LoginPage /></ErrorBoundary></Suspense>} />
        <Route path="auth/register" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><RegisterPage /></ErrorBoundary></Suspense>} />
        <Route path="auth/verify-email" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><VerifyEmailPage /></ErrorBoundary></Suspense>} />
        <Route path="auth/forgot-password" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><ForgotPasswordPage /></ErrorBoundary></Suspense>} />
        <Route path="auth/reset-password" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><ResetPasswordPage /></ErrorBoundary></Suspense>} />

        {/* App Shell — 带导航的内部页面 */}
        <Route element={<RootLayout />}>
          {/* 阅读广场 (reading mode) */}
          <Route path="plaza" element={<Suspense fallback={<PlazaFallback />}><ErrorBoundary><PlazaPage /></ErrorBoundary></Suspense>} />
          <Route
            path="plaza/novel/:novelId"
            element={
               <Suspense fallback={<ContentFallback />}>
                <ErrorBoundary><NovelDetailPage /></ErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="plaza/novel/:novelId/read/:chapterId"
            element={
               <Suspense fallback={<ContentFallback />}>
                <ErrorBoundary><ReaderPage /></ErrorBoundary>
              </Suspense>
            }
          />

          {/* Genre Pages (SEO long-tail) */}
          <Route path="genres" element={<Suspense fallback={<PlazaFallback />}><ErrorBoundary><GenreListPage /></ErrorBoundary></Suspense>} />
          <Route path="genre/:slug" element={<Suspense fallback={<PlazaFallback />}><ErrorBoundary><GenrePage /></ErrorBoundary></Suspense>} />

          {/* User Profile (public) */}
          <Route path="user/:userId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><UserProfilePage /></ErrorBoundary></Suspense>} />

          {/* Bookshelf — 我的书架 (收藏 + 浏览记录) */}
          <Route path="bookshelf" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><BookshelfPage /></ErrorBoundary></Suspense>} />

          {/* Profile Settings (requires auth) */}
          <Route path="settings/profile" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><ProfileSettingsPage /></ErrorBoundary></Suspense></ProtectedRoute>} />

          {/* Admin Dashboard (requires auth + admin) — with sub-routes */}
          <Route path="admin" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><AdminLayout /></ErrorBoundary></Suspense></ProtectedRoute>}>
            <Route index element={<Suspense fallback={<PageFallback />}><ErrorBoundary><AdminDashboardPage /></ErrorBoundary></Suspense>} />
            <Route path="review" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><ReviewQueuePage /></ErrorBoundary></Suspense>} />
            <Route path="review/:reviewId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><ReviewDetailPage /></ErrorBoundary></Suspense>} />
            <Route path="logs" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><ModActionLogPage /></ErrorBoundary></Suspense>} />
            <Route path="reports" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><ReportManagePage /></ErrorBoundary></Suspense>} />
            <Route path="users" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><UserManagePage /></ErrorBoundary></Suspense>} />
            <Route path="comments" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><CommentAuditPage /></ErrorBoundary></Suspense>} />
            <Route path="config" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><SystemConfigPage /></ErrorBoundary></Suspense>} />
            <Route path="novels" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><AdminNovelManager /></ErrorBoundary></Suspense>} />
            <Route path="users/:userId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><UserDetailPage /></ErrorBoundary></Suspense>} />
            <Route path="arena" element={<Suspense fallback={<ListFallback />}><ErrorBoundary><ArenaAdminPage /></ErrorBoundary></Suspense>} />
          </Route>

          {/* Creative Center (creative mode) — contains Workshop + Studio entry */}
          <Route path="workshop" element={<ProtectedRoute><Suspense fallback={<ListFallback />}><ErrorBoundary><CreativePanel /></ErrorBoundary></Suspense></ProtectedRoute>} />

          {/* Arena — 共创竞技场 (Phase 19A) */}
          <Route path="arena" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><ArenaHomePage /></ErrorBoundary></Suspense>} />
          <Route path="arena/round/:roundId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><RoundDetailPage /></ErrorBoundary></Suspense>} />
          <Route path="arena/seed/:seedId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><SeedDetailPage /></ErrorBoundary></Suspense>} />
          <Route path="arena/seed/:seedId/outline" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><OutlineReviewPage /></ErrorBoundary></Suspense>} />
          <Route path="arena/my" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><MyArenaPage /></ErrorBoundary></Suspense></ProtectedRoute>} />

          {/* Derivatives — 同人衍生馆 */}
          <Route path="derivatives" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><DerivativesHomePage /></ErrorBoundary></Suspense>} />
          <Route path="derivatives/source/:sourceNovelId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><SourceDerivativesPage /></ErrorBoundary></Suspense>} />
          <Route path="derivatives/tree/:novelId" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><DerivativeTreePage /></ErrorBoundary></Suspense>} />

          {/* Community — 创作者社区 (BD-08) */}
          <Route path="community" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><CommunityPage /></ErrorBoundary></Suspense>} />

          {/* Studio — 创作台 (requires auth) */}
          <Route path="studio/:novelId" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><StudioLayout /></ErrorBoundary></Suspense></ProtectedRoute>}>
            <Route index element={<Suspense fallback={<PageFallback />}><StudioIndexRedirect /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><NovelSettingsPage /></ErrorBoundary></Suspense>} />
            <Route path="seed" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><SeedPage /></ErrorBoundary></Suspense>} />
            <Route path="foundation" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><FoundationPage /></ErrorBoundary></Suspense>} />
            <Route
              path="outline"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ErrorBoundary><OutlinePage /></ErrorBoundary>
                </Suspense>
              }
            />
            <Route
              path="writing"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ErrorBoundary><WritingPage /></ErrorBoundary>
                </Suspense>
              }
            />
            <Route
              path="codex"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ErrorBoundary><CodexPage /></ErrorBoundary>
                </Suspense>
              }
            />
            <Route
              path="publish"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ErrorBoundary><PublishPage /></ErrorBoundary>
                </Suspense>
              }
            />
            <Route
              path="import-export"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ErrorBoundary><ImportExportPage /></ErrorBoundary>
                </Suspense>
              }
            />
          </Route>

          {/* 404 catch-all — within RootLayout shell */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* 404 catch-all — outside layout (for truly unknown paths) */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
    </ThemeProvider>
    </ReducedMotionProvider>
  );
}
